import ActiveToggleButton from "@/components/ActiveToggleButton";
import { BaseTable } from "@/components/BaseTable";
import CustomButton from "@/components/common/Button";
import { getTenantId } from "@/lib/storeManager";
import SLAConfigurationService from "@/services/APIs/SLAConfigurationService";
import { gridName } from "@/utils/constant/gridName";
import { tableName } from "@/utils/constant/tableName";
import { SLAConfiguration } from "@/utils/types/slaConfiguration";
import { ProColumns } from "@ant-design/pro-components";
import { useQuery } from "@tanstack/react-query";
import { Popconfirm, Tag, Tooltip } from "antd";
import dayjs from "dayjs";
import { Edit2, Plus, Trash } from "lucide-react";
import { useMemo } from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

const SLAListPage = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);

  const SD_ADMIN = "/sd/admin";

  // Read SLA schema config from localStorage
  const schemaConfig = useMemo(() => {
    const tenantId = getTenantId();
    if (!tenantId) return { sla_policy_schema: undefined, sla_status_schema: undefined };
    try {
      const raw = localStorage.getItem(`slaSchemaConfiguration:${tenantId}`);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }, []);

  // Fetch SLA configurations data
  const {
    data: slaConfigs,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["sla-configurations", page, pageSize],
    queryFn: () =>
      SLAConfigurationService.getAllConfigurations({ page, pageSize }),
  });

  const gridData = slaConfigs?.results ?? [];
  const total = slaConfigs?.count ?? 0;

  // Handle delete SLA configuration
  const handleDelete = async (id: string) => {
    setIsDeleting(id);
    try {
      await SLAConfigurationService.deleteConfiguration(id);
      refetch();
    } catch (error) {
      console.error("Delete failed:", error);
    } finally {
      setIsDeleting(null);
    }
  };

  // Check if any action is in progress
  const isAnyActionInProgress = isLoading || isDeleting !== null;

  // Table columns configuration
  const columns: ProColumns<SLAConfiguration>[] = [
  
    {
      title: "SLA Policy Schema",
      key: "sla_policy_schema",
      width: 180,
      render: () => schemaConfig.sla_policy_schema
        ? <Tooltip title={schemaConfig.sla_policy_schema}><Tag color="blue" style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis" }}>{schemaConfig.sla_policy_schema}</Tag></Tooltip>
        : <span style={{ color: "#bbb" }}>—</span>,
    },
    {
      title: "Business Hours Schema",
      key: "business_hours_schema",
      width: 180,
      render: () => schemaConfig.sla_status_schema
        ? <Tooltip title={schemaConfig.sla_status_schema}><Tag color="geekblue" style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis" }}>{schemaConfig.sla_status_schema}</Tag></Tooltip>
        : <span style={{ color: "#bbb" }}>—</span>,
    },
  
   
   
 
  
   
    
   
    {
      title: "Actions",
      width: 100,
      valueType: "option",
      render: (_, record) => [
        <CustomButton
          key="edit"
          icon={<Edit2 size={16} />}
          onClick={() => navigate(`${SD_ADMIN}/sla-configuration-edit?id=${record.id}`)}
          type="text"
          title="Edit SLA Configuration"
        />,
        <Popconfirm
          key="delete"
          title="Are you sure you want to delete this SLA configuration?"
          description="This action cannot be undone."
          onConfirm={() => handleDelete(record.id)}
          okText="Yes"
          cancelText="No"
          disabled={isAnyActionInProgress}
        >
          <CustomButton
            danger
            icon={<Trash size={16} />}
            type="text"
            title="Delete SLA Configuration"
            loading={isDeleting === record.id}
            disabled={isAnyActionInProgress}
          />
        </Popconfirm>,
      ],
    },
  ];

  return (
    <BaseTable<SLAConfiguration>
      loading={isLoading}
      columns={columns}
      dataSource={gridData}
      page={page}
      pageSize={pageSize}
      total={total}
      rowKey="id"
      toolBarRender={() => [
        <CustomButton
          key="add"
          type="primary"
          icon={<Plus size={16} />}
          onClick={() => navigate(`${SD_ADMIN}/sla-configuration-add`)}
          disabled={isAnyActionInProgress}
          title="Add New SLA Configuration"
        >
          Add SLA
        </CustomButton>,
      ]}
      headerTitle="SLA Configurations"
      onPageChange={(page, size) => {
        setPage(page);
        setPageSize(size ?? 10);
      }}
      request={() => refetch()}
      tableName={gridName.slaConfigurationTable}
    />
  );
};

export default SLAListPage;
